import { useState } from "react";
import { Logo } from "@/components/Brand";
import { SiteFooter } from "@/components/SiteFooter";
import { Play } from "lucide-react";

const DRIVE_THUMB = (id) => `https://drive.google.com/thumbnail?id=${id}&sz=w1600`;
const DRIVE_EMBED = (id) => `https://drive.google.com/file/d/${id}/preview`;

// Guides use the existing setup-video order and numbering. Do not resequence.
const GUIDES = [
  {
    n: 1,
    title: "Manage your tasks in the Command Center",
    videoId: "1naRiAZaVMNNqzlTorL6q6wK2E5YZbMD9",
    text: "This walkthrough shows how a task moves through the Command Center, from the moment you capture it to the moment it is done. You will see where to add a task, how to set its priority, and how the daily view surfaces only what matters today. Once this part clicks, most of the system runs on the same few moves.",
    transcript: [
      "This is the Command Center home surface. Everything is split into Task, Journal, and Project.",
      "In Task, Today represents the work that you plan to do today. Upcoming represents future scheduled work. It is grouped into today, tomorrow, and supports future dates like months as you add more tasks.",
      "Needs Review represents work that needs processing that is overdue or missing due dates. It is grouped into items that have no due dates, that are overdue this week, or that are overdue by a certain number of days. As you add more tasks, other groups appear for items that are unplanned or have been missing their due dates.",
      "Efforts represents how long a task is likely to take. It is grouped by estimated duration. Use it when you want a quick win or when you have a small window. This view lets you choose based on your capacity.",
      "All Tasks is the full list. It is deliberately kept to a short view of all your tasks in one place. You can see the dates for tasks that have due dates, tasks that are overdue, or tasks with no due date.",
      "To add a task, click the button here for a new task, or use the shortcut button. You can also add a subtask, and then edit the subtask.",
      "A task is work that needs an answer or outcome, something that is not done until you have reached a conclusion. A subtask is a consideration or input that feeds the decision, something you make, explore, or weigh to resolve the main task.",
      "You can drag subtasks into a To do status, a Doing status, or a Done status as you work on the main task.",
    ],
  },
  {
    n: 2,
    title: "Use journals for daily and weekly planning",
    videoId: "1h2gJeLusCOKpSVO7eA_jSE6ey_uVnyw-",
    text: "Here you will see how the daily and weekly journals fit into your routine. The daily journal is where you set one priority and check in with your energy, while the weekly view helps you look back and plan ahead without rewriting everything. Use them as lightly or as fully as suits the day.",
    transcript: [
      "Journal is where you think and reset. It is intentionally designed to be a reflection first surface, with the option to plan.",
      "You can create different types of journals. The default is a daily journal, which you can create with the new button or the shortcut button. In the daily journal you will see a set of prompts grouped into two categories, one to start the day and one to close the day.",
      "To start the day, you can brain dump anything on your mind. You can add a new task for any item that does not exist yet, or review existing tasks that need planning. Choosing Review Tasks shows you tasks that are upcoming and the ones that need to be planned or rescheduled.",
      "The Daily Reset template is grouped into two categories, Reflect and Move forward. The reflection prompts give you space to write down anything that feels heavy, and any tasks or items you would like to let go of, with a button to review tasks after reflection.",
      "Move forward has prompts for the minimum commitments for the day and the minimum next steps. View Low Effort Tasks shows you tasks with a low estimated duration to consider.",
      "The weekly overview template has two categories, Look back and Look forward. You can reflect on what worked well this week and what did not, then plan the items you would like to commit to next week.",
      "You can view tasks by week, and use the calendar to plan items or move any tasks that do not have dates.",
    ],
  },
  {
    n: 3,
    title: "Create and manage projects with tasks",
    videoId: "1sFJgeEjqLbsUYAA5hDUwKBNhgo_oB4Qw",
    text: "This guide shows how to turn a bigger goal into a project, then break it into tasks the daily view can pick up. You will see how tasks stay linked to their project so nothing floats loose, and how to check progress at a glance. It is the same task flow you already know, grouped around an outcome.",
    transcript: [
      "Projects are outcomes that need several associated tasks carried out over time. To create one, click the new button and give it a short name.",
      "You can set a status for your project, and you can set a target deadline.",
      "Projects are associated with tasks. You can create a new task and name it, then drag it across the Kanban board to set its status.",
      "You can also edit a task. If you set its due date to today, the task appears on your Today list.",
      "In the Command Center, projects are intentionally kept simple.",
    ],
  },
  {
    n: 4,
    title: "Use Notion AI with your templates",
    videoId: "1l5uz0eXlLvOOSpuAa3iDH-pfYEHaFjKQ",
    text: "This walkthrough covers where Notion AI can help inside the Command Center, from drafting a quick note to summarising what you captured. You will see how to prompt it and where it fits naturally into your existing pages. Treat it as an optional assistant, not a step you have to use.",
    transcript: [
      "You can use the template together with Notion AI for more efficiency. If Notion already has context about you from other integrations like email or messages, you may find Notion AI especially useful.",
      "You can write a prompt to get started. Without any integrations connected, a generic prompt still works. If you had integrations connected and the task needed existing information, you could prompt differently so Notion draws on that context.",
      "Notion will fetch from existing sources if needed, then create the new entries or pages for you.",
      "In this example, Notion helped break the task into actionable subtasks that you can plan around.",
    ],
  },
  {
    n: 5,
    title: "Set up your templates and settings",
    videoId: "1IbyzsWTgFvnorQxkuhmjMPt1u2PEQxYk",
    text: "This guide walks you through the first setup, from duplicating the template to adjusting the settings that shape how the system behaves. You will see which options are worth setting once and which you can leave at their defaults. A few minutes here makes everything that follows feel like yours.",
    transcript: [
      "In Notion database views, you can click the icons in the top right to configure a view or change its settings.",
      "If you ever want to change how a view behaves, you can use these settings. You can also ask Notion AI how to change the configuration.",
      "Another useful setting is the one for templates. To edit your journal template so it uses a different set of prompts, open the three dot menu and choose Edit.",
      "Change a prompt into a different question, and the next time you create a journal from that template, it will include your updates.",
    ],
  },
];

const GuideBlock = ({ guide }) => {
  const [loaded, setLoaded] = useState(false);
  const [thumbFailed, setThumbFailed] = useState(false);

  return (
    <section id={`guide-${guide.n}`} className="scroll-mt-24" data-testid={`guide-${guide.n}`}>
      <h2 className="font-heading text-2xl font-extrabold text-white sm:text-3xl">
        Guide {guide.n}: {guide.title}
      </h2>

      <div className="mt-5 overflow-hidden rounded-2xl border border-white/10 bg-[#14213D] shadow-[0_12px_40px_rgba(0,0,0,0.35)]">
        <div className="relative aspect-video w-full">
          {loaded ? (
            <iframe
              src={DRIVE_EMBED(guide.videoId)}
              title={`Guide ${guide.n}: ${guide.title}`}
              allow="autoplay; fullscreen"
              allowFullScreen
              className="absolute inset-0 h-full w-full"
              data-testid={`guide-${guide.n}-iframe`}
            />
          ) : (
            <button
              type="button"
              onClick={() => setLoaded(true)}
              data-testid={`guide-${guide.n}-play`}
              aria-label={`Play the video for Guide ${guide.n}, ${guide.title}`}
              className="group absolute inset-0 h-full w-full"
            >
              {!thumbFailed ? (
                <img
                  src={DRIVE_THUMB(guide.videoId)}
                  onError={() => setThumbFailed(true)}
                  alt={`Video thumbnail for Guide ${guide.n}, ${guide.title}`}
                  className="h-full w-full object-cover"
                />
              ) : (
                <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-[#14213D] via-[#0F1B32] to-[#0B132B] px-6 text-center">
                  <span className="font-heading text-lg font-bold text-[#9FB2C9]">{guide.title}</span>
                </div>
              )}
              <span className="absolute inset-0 bg-[#0B132B]/35 transition-colors duration-200 group-hover:bg-[#0B132B]/20" />
              <span className="absolute left-1/2 top-1/2 flex h-16 w-16 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-[#F5A623] text-[#0B132B] shadow-lg transition-transform duration-200 group-hover:scale-105">
                <Play className="ml-0.5 h-7 w-7 fill-current" />
              </span>
            </button>
          )}
        </div>
      </div>

      {!loaded && (
        <p className="mt-2 text-xs text-[#6E829B]" data-testid={`guide-${guide.n}-loadnote`}>
          The video loads only when you press play, so this page stays light.
        </p>
      )}

      <p className="mt-4 text-[15px] leading-relaxed text-[#C7D3E2]">{guide.text}</p>

      {guide.transcript && (
        <details className="mt-4 rounded-xl border border-white/10 bg-[#14213D]/60" data-testid={`guide-${guide.n}-transcript`}>
          <summary className="cursor-pointer list-none px-4 py-3 font-heading text-sm font-semibold text-[#A8D5F5] transition-colors hover:text-white">
            Read the transcript
          </summary>
          <div className="space-y-3 px-4 pb-4 pt-1">
            {guide.transcript.map((para, i) => (
              <p key={i} className="text-sm leading-relaxed text-[#9FB2C9]">{para}</p>
            ))}
          </div>
        </details>
      )}
    </section>
  );
};

export default function VideoGuides() {
  return (
    <div className="min-h-screen bg-[#0B132B]" data-testid="video-guides-page">
      <header className="sticky top-0 z-50 border-b border-white/10 bg-[#0B132B]/85 backdrop-blur-md">
        <div className="mx-auto flex max-w-[760px] items-center justify-between px-6 py-3">
          <Logo imgClass="h-10 sm:h-11" />
          <a href="/community" className="font-heading text-sm font-semibold text-[#9FB2C9] transition-colors hover:text-white" data-testid="guides-nav-community">
            Community
          </a>
        </div>
      </header>

      <main className="mx-auto max-w-[760px] px-6 py-14">
        <p className="font-heading text-xs font-bold uppercase tracking-[0.2em] text-[#F5A623]">Setup</p>
        <h1 className="mt-3 font-heading text-4xl font-extrabold leading-tight text-white sm:text-5xl">Video guides</h1>
        <p className="mt-4 max-w-[560px] text-base leading-relaxed text-[#9FB2C9]">
          The setup guides are short video walkthroughs that show you how to install and use your Command Center step by step. Watch them in any order, and come back whenever you need a refresher. Each one loads only when you press play.
        </p>

        <div className="mt-12 space-y-16">
          {GUIDES.map((g) => (
            <GuideBlock key={g.n} guide={g} />
          ))}
        </div>
      </main>

      <SiteFooter />
    </div>
  );
}
